import React from "react";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import Beam from "./Beam";
import { DoorGlassMaterial } from "shared/utils/door";
import {
  defaultDoorWidth,
  defaultSideFrameWidth,
  defaultUHandleConnectorXPosition,
  defaultUHandleHeight,
  defaultUHandleZPosition,
} from "shared/utils/doorSceneConstants";
import { getGeometryBounds2D, getOpeningAnchoredX } from "./openingGeometry";

interface HandleProps {
  doorNodes: any;
  materials: DoorGlassMaterial;
  reverse?: boolean;
  index: number;
  shiftXValue?: number;
  schuifdeur?: boolean;
  showBackHandle?: boolean;
}

const UHandle: React.FC<HandleProps> = ({
  doorNodes,
  materials,
  reverse = false,
  index,
  shiftXValue,
  schuifdeur = false,
  showBackHandle = true,
}: HandleProps) => {
  const {
    getConfigurationAnchorBeams,
    getConfigurationWidth,
    getConfigurationColor,
  } = useConfigurationOptionsContext();

  const anchorBeams = getConfigurationAnchorBeams();
  const topBeam = anchorBeams[0] ?? null;
  const bottomBeam = anchorBeams[1] ?? null;
  const topBeamY = (topBeam?.height ?? 1280) / 1000;
  const bottomBeamY = (bottomBeam?.height ?? 640) / 1000;
  const width = getConfigurationWidth(index);
  const glassBounds = getGeometryBounds2D(doorNodes.glass.geometry);
  const defaultConnectorX = reverse
    ? (defaultDoorWidth / 2 -
        (defaultSideFrameWidth + defaultUHandleConnectorXPosition)) /
      1000
    : -(
        defaultDoorWidth / 2 -
        (defaultSideFrameWidth + defaultUHandleConnectorXPosition)
      ) / 1000;
  const handleConnectorX = getOpeningAnchoredX({
    glassBounds,
    width,
    defaultX: defaultConnectorX,
    reverse,
  });
  const handleScaleY =
    (topBeamY * 1000 - bottomBeamY * 1000) / defaultUHandleHeight;
  const handlePositionY = bottomBeamY + (topBeamY - bottomBeamY) / 2;

  return (
    <group>
      {/* Handle top beam */}
      <Beam
        id={topBeam?.id ?? null}
        key={`u-handle-beam-0`}
        doorNodes={doorNodes}
        materials={materials}
        width={width}
        height={topBeamY}
      />
      {/* Handle bottom beam */}
      <Beam
        id={bottomBeam?.id ?? null}
        key={`u-handle-beam-1`}
        doorNodes={doorNodes}
        materials={materials}
        width={width}
        height={bottomBeamY}
      />
      <group position-x={shiftXValue ?? 0}>
        {schuifdeur === false && (
          <group>
            {/* Handle top part - front */}
            <mesh
              geometry={doorNodes["handle_ugreep_handle_0_front"].geometry}
              material={materials[getConfigurationColor()]}
              position={[
                handleConnectorX,
                topBeamY,
                showBackHandle ? 0.022 : 0,
              ]}
            ></mesh>
            {/* Handle middle part - front */}
            <mesh
              geometry={doorNodes["handle_ugreep_handle_1_front"].geometry}
              material={materials[getConfigurationColor()]}
              position={[
                handleConnectorX,
                handlePositionY,
                -(defaultUHandleZPosition / 1000),
              ]}
              scale={[1, handleScaleY, 1]}
            ></mesh>
            {/* Handle bottom part - front */}
            <mesh
              geometry={doorNodes["handle_ugreep_handle_2_front"].geometry}
              material={materials[getConfigurationColor()]}
              position={[
                handleConnectorX,
                bottomBeamY,
                showBackHandle ? 0.022 : 0,
              ]}
            ></mesh>
          </group>
        )}
        {/* Handle top part - back */}
        {showBackHandle && (
          <group position-x={schuifdeur ? 0.05 : 0}>
            <mesh
              geometry={doorNodes["handle_ugreep_handle_0_back"].geometry}
              material={materials[getConfigurationColor()]}
              position={[handleConnectorX, topBeamY, schuifdeur ? 0 : -0.022]}
            ></mesh>
            {/* Handle middle part - back */}
            <mesh
              geometry={doorNodes["handle_ugreep_handle_1_back"].geometry}
              material={materials[getConfigurationColor()]}
              position={[
                handleConnectorX,
                handlePositionY,
                defaultUHandleZPosition / 1000,
              ]}
              scale={[1, handleScaleY, 1]}
            ></mesh>
            {/* Handle bottom part - back */}
            <mesh
              geometry={doorNodes["handle_ugreep_handle_2_back"].geometry}
              material={materials[getConfigurationColor()]}
              position={[
                handleConnectorX,
                bottomBeamY,
                schuifdeur ? 0 : -0.022,
              ]}
            ></mesh>
          </group>
        )}
      </group>
    </group>
  );
};

export default UHandle;
